import type {
  BlobStorage,
  DocStorage,
  ListedBlobRecord,
} from '@affine/nbstore';
import type { UnboundWorkerInitOptions } from '@affine/nbstore/worker/client';
import type { Workspace as BSWorkspace } from '@blocksuite/affine/store';
import { createIdentifier, type LiveData } from '@toeverything/infra';

import type { WorkspaceProfileInfo } from '../entities/profile';
import type { Workspace } from '../entities/workspace';
import type { WorkspaceMetadata } from '../metadata';
import type { WorkspaceOpenOptions } from '../open-options';

export interface LocalEraseOptions {
  /**
   * Erase for this recovery only: a copy already rebuilt on that recovery
   * version, by another tab or window, stays.
   */
  recoveryVersion?: number;
}

export interface WorkspaceFlavourProvider {
  flavour: string;

  deleteWorkspace(id: string): Promise<void>;

  /**
   * Erase this device's copy of a workspace the account lost access to:
   * every local storage and search index, independent of the platform.
   */
  eraseLocalData?(id: string, options?: LocalEraseOptions): Promise<void>;

  createWorkspace(
    initial: (
      docCollection: BSWorkspace,
      blobStorage: BlobStorage,
      docStorage: DocStorage
    ) => Promise<void>,
    name?: string,
    locale?: string
  ): Promise<WorkspaceMetadata>;

  workspaces$: LiveData<WorkspaceMetadata[]>;

  /**
   * means the workspace list is loading. if it's true, the workspace page will show loading spinner.
   */
  isRevalidating$?: LiveData<boolean>;

  /**
   * revalidate the workspace list.
   *
   * will be called when user open workspace list, or workspace not found.
   */
  revalidate?: () => void;

  getWorkspaceProfile(
    id: string,
    signal?: AbortSignal
  ): Promise<WorkspaceProfileInfo | undefined>;

  getWorkspaceBlob(id: string, blob: string): Promise<Blob | null>;

  listBlobs(workspaceId: string): Promise<ListedBlobRecord[]>;

  deleteBlob(
    workspaceId: string,
    blob: string,
    permanent: boolean
  ): Promise<void>;

  getEngineWorkerInitOptions(
    workspaceId: string,
    metadata?: WorkspaceMetadata,
    openOptions?: WorkspaceOpenOptions
  ): UnboundWorkerInitOptions;

  onWorkspaceInitialized?(workspace: Workspace): void;
}

export interface WorkspaceFlavoursProvider {
  workspaceFlavours$: LiveData<WorkspaceFlavourProvider[]>;
}

export const WorkspaceFlavoursProvider =
  createIdentifier<WorkspaceFlavoursProvider>('WorkspaceFlavoursProvider');
